import { standardId } from '../../utils/standardRef'

// Pure Library-filter logic, shared by every window's nav state. A window's
// filter is plain serializable data
// ({ values, text, matchMode, bookmarkedOnly, includeTopicDescendants });
// `makeMatchers` turns it into the two predicates the tree derivation uses:
//   - matchesItem     inside a standard, all facets apply
//   - matchesStandard the root list, where author / text type / topic apply
// Kept framework-free so it can run per active window without extra hooks.

export const emptyValues = () => ({
  standards: [], // [{ _id, label, standard, ... }]
  authors: [], // [{ _id, label }]
  types: [], // [{ _id, label }]
  topics: [], // [{ _id, name }]
})

export const emptyFilter = () => ({
  values: emptyValues(),
  text: '',
  matchMode: 'or', // 'or' | 'and'
  bookmarkedOnly: false,
  // When set, a selected topic also matches bookmarks filed under topics inside it.
  includeTopicDescendants: false,
})

// Normalize a persisted (possibly partial) filter blob into a full filter.
export function coerceFilter(raw) {
  if (!raw || typeof raw !== 'object') return emptyFilter()
  return {
    values: { ...emptyValues(), ...(raw.values || {}) },
    text: typeof raw.text === 'string' ? raw.text : '',
    matchMode: raw.matchMode === 'and' ? 'and' : 'or',
    bookmarkedOnly: Boolean(raw.bookmarkedOnly),
    includeTopicDescendants: Boolean(raw.includeTopicDescendants),
  }
}

const idSet = (arr, pick) => new Set((arr || []).map((x) => String(pick(x))))
const some = (arr, set) => (arr || []).some((x) => set.has(String(x?._id ?? x)))
const topicKey = (value) => {
  if (value && typeof value === 'object') return String(value._id || '')
  return String(value || '')
}

// Selected topics, plus every topic whose ancestor path contains one of them.
function topicScope(selected, topics, includeDescendants) {
  const ids = new Set((selected || []).map((topic) => topicKey(topic?._id ?? topic)).filter(Boolean))
  if (!includeDescendants || !ids.size) return ids
  ;(topics || []).forEach((topic) => {
    const id = topicKey(topic?._id)
    if (!id) return
    if ((topic.path || []).some((entry) => ids.has(topicKey(entry)))) ids.add(id)
  })
  return ids
}

// Split the search text into lowercase terms. A label matches when it contains
// EVERY term as a substring, in any order — so "reb cover" matches "cover for
// rebar" (each term found independently, not a phrase match).
const termsOf = (text) => (text || '').trim().toLowerCase().split(/\s+/).filter(Boolean)
const labelMatches = (label, terms) => {
  if (!terms.length) return true
  const hay = (label || '').toLowerCase()
  return terms.every((t) => hay.includes(t))
}

export function isFilterActive(filter) {
  const f = filter || emptyFilter()
  const v = f.values || emptyValues()
  return Boolean(
    termsOf(f.text).length ||
      v.authors.length ||
      v.types.length ||
      v.topics.length ||
      f.bookmarkedOnly
  )
}

// Build the predicates for one filter. `bookmarks` is the useBookmarks() result,
// needed for the topic + "bookmarked only" facets. `topics` is the owner's topic
// tree, used to roll a selected topic down to the topics inside it.
export function makeMatchers(filter, bookmarks, topics) {
  const f = coerceFilter(filter)
  const { values, matchMode, bookmarkedOnly, includeTopicDescendants } = f
  const terms = termsOf(f.text)
  const byItem = bookmarks?.byItem || {}

  const authorIds = idSet(values.authors, (a) => a._id)
  const types = new Set(
    values.types.map((t) => (t && typeof t === 'object' ? String(t._id) : String(t || ''))).filter(Boolean)
  )
  const topicIds = topicScope(values.topics, topics, includeTopicDescendants)

  // Roll bookmarks up to the standard level so the root list can filter by
  // "bookmarked only" and by topic: a standard qualifies when it holds at least
  // one bookmarked item (in the selected topic). Each bookmark's item is
  // populated with its `standard`, and `topics` with { _id }.
  const bookmarkedStandards = new Set()
  const topicsByStandard = new Map()
  Object.values(byItem).forEach((bm) => {
    const std = standardId(bm?.item?.standard)
    if (!std) return
    bookmarkedStandards.add(std)
    if (bm.topics?.length) {
      let set = topicsByStandard.get(std)
      if (!set) {
        set = new Set()
        topicsByStandard.set(std, set)
      }
      bm.topics.forEach((t) => set.add(String(t?._id ?? t)))
    }
  })

  const combine = (checks) => {
    if (!checks.length) return true
    return matchMode === 'and' ? checks.every(Boolean) : checks.some(Boolean)
  }

  // Inside a standard: every facet applies. The label text search is always an
  // additional AND constraint (independent of the Any/All facet mode).
  const matchesItem = (item) => {
    if (!labelMatches(item.label, terms)) return false
    const checks = []
    if (authorIds.size) checks.push(some(item.standard?.authors, authorIds))
    if (types.size) checks.push(types.has(standardId(item.standard?.textType)))
    if (topicIds.size) {
      const bm = byItem[item._id]
      checks.push(Boolean(bm && some(bm.topics, topicIds)))
    }
    if (bookmarkedOnly) checks.push(Boolean(byItem[item._id]))
    return combine(checks)
  }

  // Root standards list: author / text type / topic / bookmarked-only apply,
  // plus the label text search (against the standard name). Topic and
  // bookmarked-only match a standard by whether it contains a qualifying
  // bookmarked item.
  const matchesStandard = (std) => {
    if (!labelMatches(std.label, terms)) return false
    const checks = []
    if (authorIds.size) checks.push(some(std.authors, authorIds))
    if (types.size) checks.push(types.has(standardId(std.textType)))
    if (topicIds.size) {
      const set = topicsByStandard.get(standardId(std.standard))
      checks.push(Boolean(set && [...topicIds].some((id) => set.has(id))))
    }
    if (bookmarkedOnly) checks.push(bookmarkedStandards.has(standardId(std.standard)))
    return combine(checks)
  }

  return { active: isFilterActive(f), matchesItem, matchesStandard }
}
